import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Grid, Card, Typography, CircularProgress, Alert,
  LinearProgress, Chip,
} from "@mui/material";
import { API_BASE_URL } from "../../config";

const CLOUD_COLOR = { aws: "#FF9900", gcp: "#4285F4", azure: "#0089D6", private: "#757575" };
const TIER_COLOR  = { tenant: "#1976d2", enterprise: "#7b1fa2", multitenant: "#00838f" };
const STATUS_COLOR = { active: "#2e7d32", suspended: "#d32f2f", expired: "#ed6c02", pending: "#757575" };

function StatCard({ label, value, sub, color }) {
  return (
    <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff" }}>
      <Typography fontSize="13px" color="text.secondary" mb={0.5}>{label}</Typography>
      <Typography fontSize="28px" fontWeight={700} color={color || "#344054"}>{value ?? "—"}</Typography>
      {sub && <Typography fontSize="12px" color="text.secondary" mt={0.5}>{sub}</Typography>}
    </Card>
  );
}

function BreakdownBar({ title, data, colorMap }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  return (
    <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff" }}>
      <Typography fontWeight={700} fontSize="14px" color="#344054" mb={2}>{title}</Typography>
      {total === 0 ? (
        <Typography fontSize="13px" color="text.secondary">No data</Typography>
      ) : (
        Object.entries(data).map(([key, count]) => {
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const color = colorMap[key] || "#9e9e9e";
          return (
            <Box key={key} mb={1.5}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color }} />
                  <Typography fontSize="13px" textTransform="capitalize">{key}</Typography>
                </Box>
                <Typography fontSize="13px" fontWeight={600}>{count} ({pct}%)</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: color + "22",
                  "& .MuiLinearProgress-bar": { bgcolor: color },
                }}
              />
            </Box>
          );
        })
      )}
    </Card>
  );
}

function SeatUsageCard({ tenants }) {
  if (!tenants?.length) return null;
  const sorted = [...tenants].sort((a, b) => {
    const pa = a.seat_count > 0 ? (a.seats_used / a.seat_count) : 0;
    const pb = b.seat_count > 0 ? (b.seats_used / b.seat_count) : 0;
    return pb - pa;
  }).slice(0, 8);

  return (
    <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff" }}>
      <Typography fontWeight={700} fontSize="14px" color="#344054" mb={2}>
        Top Tenants by Seat Usage
      </Typography>
      {sorted.map((t) => {
        const pct = t.seat_count > 0 ? Math.min(100, Math.round((t.seats_used / t.seat_count) * 100)) : 0;
        const warn = pct >= (t.warning_threshold || 80);
        return (
          <Box key={t.id} mb={1.5}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography fontSize="13px" fontWeight={500}>{t.org_name}</Typography>
              <Typography fontSize="12px" color={warn ? "warning.main" : "text.secondary"}>
                {t.seats_used ?? 0}/{t.seat_count} ({pct}%)
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: 6,
                borderRadius: 3,
                "& .MuiLinearProgress-bar": { bgcolor: warn ? "#ed6c02" : "#1976d2" },
              }}
            />
          </Box>
        );
      })}
    </Card>
  );
}

function ExpiryCard({ tenants }) {
  if (!tenants?.length) return null;
  const now = new Date();
  const expiring = tenants
    .filter((t) => t.contract_end)
    .map((t) => ({
      ...t,
      daysLeft: Math.ceil((new Date(t.contract_end) - now) / (1000 * 60 * 60 * 24)),
    }))
    .filter((t) => t.daysLeft <= 60)
    .sort((a, b) => a.daysLeft - b.daysLeft)
    .slice(0, 8);

  return (
    <Card elevation={0} sx={{ border: "1px solid #e8e8e8", borderRadius: 2, p: 3, bgcolor: "#fff" }}>
      <Typography fontWeight={700} fontSize="14px" color="#344054" mb={2}>
        Expiring Soon (&le;60 days)
      </Typography>
      {expiring.length === 0 ? (
        <Typography fontSize="13px" color="text.secondary">No tenants expiring within 60 days.</Typography>
      ) : (
        expiring.map((t) => (
          <Box key={t.id} sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            py: 1, borderBottom: "1px solid #f0f0f0",
          }}>
            <Box>
              <Typography fontSize="13px" fontWeight={500}>{t.org_name}</Typography>
              <Typography fontSize="12px" color="text.secondary">{t.slug}</Typography>
            </Box>
            <Chip
              label={t.daysLeft < 0 ? "Expired" : `${t.daysLeft}d`}
              size="small"
              sx={{
                bgcolor: t.daysLeft < 0 ? "#fdecea" : t.daysLeft <= 14 ? "#fff3e0" : "#e8f5e9",
                color: t.daysLeft < 0 ? "#d32f2f" : t.daysLeft <= 14 ? "#e65100" : "#2e7d32",
                fontWeight: 700,
              }}
            />
          </Box>
        ))
      )}
    </Card>
  );
}

export default function PlatformAnalyticsPage() {
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      axios.get(`${API_BASE_URL}/superadmin/stats`),
      axios.get(`${API_BASE_URL}/superadmin/tenants`),
    ])
      .then(([sRes, tRes]) => {
        setStats(sRes.data);
        setTenants(tRes.data.tenants || []);
      })
      .catch(() => setError("Failed to load analytics."))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  return (
    <Box sx={{ bgcolor: "#f9fafb", px: 4, py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={0.5}>Platform Analytics</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Overview of all tenants, seats, and licence health
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* KPI row */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatCard label="Total Tenants" value={stats?.total_tenants} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Active Tenants" value={stats?.active_tenants} color="#2e7d32" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard label="Total Seats Issued" value={stats?.total_seats_issued} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            label="Expiring in 30d"
            value={tenants.filter(t => {
              if (!t.contract_end) return false;
              const d = Math.ceil((new Date(t.contract_end) - new Date()) / 86400000);
              return d >= 0 && d <= 30;
            }).length}
            color="#ed6c02"
          />
        </Grid>
      </Grid>

      {/* Breakdown charts */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={4}>
          <BreakdownBar
            title="Tenants by Cloud"
            data={stats?.by_cloud || {}}
            colorMap={CLOUD_COLOR}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <BreakdownBar
            title="Tenants by Tier"
            data={stats?.by_tier || {}}
            colorMap={TIER_COLOR}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <BreakdownBar
            title="Tenants by Status"
            data={stats?.by_status || {}}
            colorMap={STATUS_COLOR}
          />
        </Grid>
      </Grid>

      {/* Seat usage + expiry */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SeatUsageCard tenants={tenants} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ExpiryCard tenants={tenants} />
        </Grid>
      </Grid>
    </Box>
  );
}
